import React, { useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Paper,
    IconButton,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { tripLogSelector, tripLogLoadingSelector } from '../../../features/tripcenter/tripControlSlice';
import { VariableSizeList as VirtualList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { getTripLog } from '../../../features/tripcenter/tripsApi';
import { Refresh } from '@mui/icons-material';

const MAX_ROWS = 1000;

const TripLogModal = ({ open, onClose, tripName, tripId }) => {
    const tripLog = useSelector(tripLogSelector);
    const isLoading = useSelector(tripLogLoadingSelector);
    const dispatch = useDispatch();

    const parsedData = useMemo(() => {
        if (!tripLog) return [];

        return tripLog.split('\n')
            .filter(row => row.trim())
            .slice(-MAX_ROWS) // Take only last 1000 rows
            .map(row => {
                // Parse log format: [17/07 10:08:20] [INFO] [MH04KF9152-30Mar] Last path metrics check: 2025-07-17T04:29:02.808Z
                const timestampMatch = row.match(/\[([^\]]+)\]/);
                const logTypeMatch = row.match(/\[([^\]]+)\]\s*\[([^\]]+)\]/);
                const truckTripMatch = row.match(/\[([^\]]+)\]\s*\[([^\]]+)\]\s*\[([^\]]+)\]/);

                if (timestampMatch && logTypeMatch && truckTripMatch) {
                    const timestamp = timestampMatch[1];
                    const logType = logTypeMatch[2];
                    const truckAndTrip = truckTripMatch[3];
                    const logText = row.substring(row.indexOf(truckAndTrip) + truckAndTrip.length + 2).trim();

                    return {
                        timestamp,
                        logType,
                        truckAndTrip,
                        logText
                    };
                } else {
                    // Fallback for malformed logs
                    return {
                        timestamp: '',
                        logType: '',
                        truckAndTrip: '',
                        logText: row
                    };
                }
            });
    }, [tripLog]);

    // Row renderer for virtualized list
    const Row = React.memo(({ index, style }) => {
        const row = parsedData[index];
        return (
            <div style={{
                ...style,
                display: 'flex',
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                backgroundColor: index % 2 ? 'rgba(0, 0, 0, 0.02)' : 'white',
                minHeight: 40, // minimum height
            }}>
                <div style={{
                    width: '150px',
                    padding: 8,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem'
                }}>
                    {row.timestamp}
                </div>
                <div style={{
                    width: '80px',
                    padding: 8,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem',
                    fontWeight: row.logType === 'ERROR' ? 'bold' : 'normal',
                    color: row.logType === 'ERROR' ? '#d32f2f' : row.logType === 'INFO' ? '#1976d2' : 'inherit'
                }}>
                    {row.logType}
                </div>
                <div style={{
                    width: '180px',
                    padding: 8,
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    wordBreak: 'break-word',
                    fontSize: '0.875rem'
                }}>
                    {row.truckAndTrip}
                </div>
                <div style={{
                    flex: 1,
                    padding: 8,
                    wordBreak: 'break-word',
                    fontSize: '0.875rem'
                }}>
                    {row.logText}
                </div>
            </div>
        );
    });

    // Function to calculate row height based on content
    const getRowHeight = index => {
        const row = parsedData[index];
        const timestampLines = Math.ceil(row.timestamp.length / 15);
        const logTypeLines = Math.ceil(row.logType.length / 8);
        const truckTripLines = Math.ceil(row.truckAndTrip.length / 18);
        const logTextLines = Math.ceil(row.logText.length / 60);
        const lines = Math.max(timestampLines, logTypeLines, truckTripLines, logTextLines);
        return Math.max(40, lines * 20); // minimum 40px, 20px per line
    };

    // Header component
    const TableHeader = () => (
        <div style={{
            display: 'flex',
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
            position: 'sticky',
            top: 0,
            zIndex: 1,
        }}>
            <div style={{ width: '150px', padding: 8, fontWeight: 'bold', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                Timestamp
            </div>
            <div style={{ width: '80px', padding: 8, fontWeight: 'bold', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                Type
            </div>
            <div style={{ width: '180px', padding: 8, fontWeight: 'bold', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                Truck & Trip
            </div>
            <div style={{ flex: 1, padding: 8, fontWeight: 'bold' }}>
                Log Text
            </div>
        </div>
    );

    const handleRefresh = () => {
        if (!tripId) return;
        dispatch(getTripLog(tripId));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'darkgrey' }}>
                <span>Trip Log: {tripName}</span>
                <IconButton
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{ ml: 2 }}
                >
                    <Refresh />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ height: '70vh', p: 0 }}>
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <Paper sx={{ height: '100%', overflow: 'hidden' }}>
                        <TableHeader />
                        <AutoSizer>
                            {({ height, width }) => (
                                <VirtualList
                                    height={height - 48}
                                    width={width}
                                    itemCount={parsedData.length}
                                    itemSize={getRowHeight}
                                    initialScrollOffset={
                                        parsedData.reduce((total, _, index) =>
                                            total + getRowHeight(index), 0) - (height - 48)
                                    }
                                >
                                    {Row}
                                </VirtualList>
                            )}
                        </AutoSizer>
                    </Paper>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TripLogModal;